import React, { useEffect, useMemo, useState } from "react";
import {
  getBillingAlerts,
  performBillingAlertAction,
  previewBillingAlertAction,
} from "../../utils/billing";
import { notify } from "../../utils/utils";
import {
  getAllWorkspaceNames,
  getWorkspaceDetails,
} from "../../utils/workspaces";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const pad = (value: number) => value.toString().padStart(2, "0");

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const actionLabels: Record<string, string> = {
  SEND_CUSTOMER_EMAIL: "Send Email",
  MARK_RESOLVED: "Resolve",
  IGNORE: "Ignore",
  RETRY_PAYMENT: "Retry Payment",
  OPEN_HOSTED_INVOICE: "Open Invoice",
  NOTIFY_ADMIN: "Notify Admin",
  MARK_REVIEWED: "Mark Reviewed",
};

const actionTooltips: Record<string, string> = {
  SEND_CUSTOMER_EMAIL:
    "Preview and send the billing alert email to the customer.",
  MARK_RESOLVED: "Mark this billing alert as resolved.",
  IGNORE: "Ignore this billing alert without taking billing action.",
  RETRY_PAYMENT: "Retry payment collection for the linked Stripe invoice.",
  OPEN_HOSTED_INVOICE: "Open the linked Stripe hosted invoice in a new tab.",
  NOTIFY_ADMIN:
    "Preview and send this billing alert email to configured admin recipients.",
  MARK_REVIEWED: "Mark this billing alert as reviewed and resolved.",
};

const hasInvoiceUrlField = (alert: any) =>
  Object.prototype.hasOwnProperty.call(alert, "actionUrl") ||
  Object.prototype.hasOwnProperty.call(alert, "hostedInvoiceUrl");

const getInvoiceUrl = (alert: any) => alert.actionUrl || alert.hostedInvoiceUrl;

const getActionsForAlert = (alert: any) => {
  const invoiceAction = hasInvoiceUrlField(alert) && getInvoiceUrl(alert)
    ? ["OPEN_HOSTED_INVOICE"]
    : [];

  switch (alert.alertType) {
    case "PAYMENT_ACTION_REQUIRED":
      return [
        "SEND_CUSTOMER_EMAIL",
        ...invoiceAction,
        "MARK_RESOLVED",
        "IGNORE",
      ];
    case "PAYMENT_FAILED":
      return [
        "SEND_CUSTOMER_EMAIL",
        "RETRY_PAYMENT",
        ...invoiceAction,
        "MARK_RESOLVED",
        "IGNORE",
      ];
    case "UPCOMING_INVOICE":
      return ["SEND_CUSTOMER_EMAIL", ...invoiceAction, "IGNORE"];
    case "SUBSCRIPTION_CANCELLED":
      return ["NOTIFY_ADMIN", "MARK_REVIEWED"];
    default:
      return ["MARK_RESOLVED", "IGNORE"];
  }
};

const getStatusClassName = (status: string) => {
  if (status === "OPEN") return "bg-[#fff7ed] text-[#9a3412]";
  if (status === "RESOLVED") return "bg-[#ecfdf3] text-[#027a48]";
  if (status === "IGNORED") return "bg-[#f4f4f5] text-[#52525b]";
  return "bg-[#eef4ff] text-[#3538cd]";
};

const confirmationActions = new Set(["SEND_CUSTOMER_EMAIL", "NOTIFY_ADMIN"]);

const buildWorkspaceNameMap = (workspaces: any[] = []) =>
  workspaces.reduce<Record<string, string>>((map, workspace) => {
    const id = workspace.id || workspace._id;
    if (id) {
      map[id] = workspace.name || "-";
    }
    return map;
  }, {});

export default function BillingAlerts() {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [workspaceNames, setWorkspaceNames] = useState<Record<string, string>>(
    {},
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    alert: any;
    action: string;
    preview: any;
    metadata?: any;
  } | null>(null);
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [limit, setLimit] = useState(20);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({
    status: "",
    alertType: "",
    severity: "",
    workspaceId: "",
  });

  const cleanedFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value),
      ),
    [filters],
  );

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const [response, workspaceResponse] = await Promise.all([
        getBillingAlerts({
          ...cleanedFilters,
          limit,
          skip,
        }),
        getAllWorkspaceNames().catch((error) => {
          console.error("Error fetching workspace names:", error);
          return { workspaces: [] };
        }),
      ]);
      setAlerts(response.alerts || response.data || []);
      setWorkspaceNames(
        buildWorkspaceNameMap(workspaceResponse.workspaces || []),
      );
      setTotal(response.total || 0);
    } catch (error) {
      console.error("Error fetching billing alerts:", error);
      setAlerts([]);
      setWorkspaceNames({});
      notify("Failed to fetch billing alerts", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [cleanedFilters, limit, skip]);

  const handleFilterChange = (key: string, value: string) => {
    setSkip(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const buildActionMetadata = async (alert: any) => {
    if (!alert.workspaceId) return {};

    try {
      const workspaceResponse = await getWorkspaceDetails(alert.workspaceId);
      return { workspaceDetails: workspaceResponse?.data || workspaceResponse };
    } catch (error) {
      console.error("Error fetching workspace details for billing alert:", error);
      return {};
    }
  };

  const handleAction = async (alert: any, action: string) => {
    if (action === "OPEN_HOSTED_INVOICE") {
      const url = getInvoiceUrl(alert);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        notify("No invoice URL available", "error");
      }
      return;
    }

    try {
      setActionLoadingId(`${alert._id}-${action}`);

      if (confirmationActions.has(action)) {
        const metadata = await buildActionMetadata(alert);
        const response = await previewBillingAlertAction(alert._id, action, metadata);
        setConfirmation({
          alert,
          action,
          preview: response.data || response,
          metadata,
        });
        return;
      }

      await performBillingAlertAction(alert._id, action);
      notify("Billing alert action completed", "success");
      fetchAlerts();
    } catch (error) {
      console.error("Error performing billing alert action:", error);
      notify("Failed to perform billing alert action", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmation) return;

    try {
      const { alert, action, metadata } = confirmation;
      setActionLoadingId(`${alert._id}-${action}`);
      await performBillingAlertAction(alert._id, action, metadata || {});
      notify("Billing alert action completed", "success");
      setConfirmation(null);
      fetchAlerts();
    } catch (error) {
      console.error("Error performing billing alert action:", error);
      notify("Failed to perform billing alert action", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-4 p-6">
      <div>
        <h1 className="text-[24px] font-semibold text-[#211f26]">
          Billing Alerts
        </h1>
        <p className="text-[13px] text-[#84828e]">
          Stripe billing issues and operational alerts.
        </p>
      </div>

      <div className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className="mb-4 grid grid-cols-4 gap-3">
          <select
            className="h-10 rounded-md border border-[#eae7ec] px-3 text-[14px]"
            value={filters.status}
            onChange={(event) => handleFilterChange("status", event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="OPEN">Open</option>
            <option value="EMAIL_SENT">Email sent</option>
            <option value="REMINDER_SENT">Reminder sent</option>
            <option value="RESOLVED">Resolved</option>
            <option value="IGNORED">Ignored</option>
          </select>

          <select
            className="h-10 rounded-md border border-[#eae7ec] px-3 text-[14px]"
            value={filters.alertType}
            onChange={(event) =>
              handleFilterChange("alertType", event.target.value)
            }
          >
            <option value="">All alert types</option>
            <option value="PAYMENT_ACTION_REQUIRED">Payment action required</option>
            <option value="PAYMENT_FAILED">Payment failed</option>
            <option value="PAYMENT_OVERDUE">Payment overdue</option>
            <option value="UPCOMING_INVOICE">Upcoming invoice</option>
            <option value="SUBSCRIPTION_UPDATED">Subscription updated</option>
            <option value="SUBSCRIPTION_CANCELLED">Subscription cancelled</option>
          </select>

          <select
            className="h-10 rounded-md border border-[#eae7ec] px-3 text-[14px]"
            value={filters.severity}
            onChange={(event) =>
              handleFilterChange("severity", event.target.value)
            }
          >
            <option value="">All severities</option>
            <option value="CRITICAL">Critical</option>
            <option value="WARNING">Warning</option>
            <option value="INFO">Info</option>
          </select>

          <input
            className="h-10 rounded-md border border-[#eae7ec] px-3 text-[14px]"
            placeholder="Workspace ID"
            value={filters.workspaceId}
            onChange={(event) =>
              handleFilterChange("workspaceId", event.target.value)
            }
          />
        </div>

        <div className="mb-4 flex items-center justify-between text-[13px] text-[#5f5a66]">
          <div>
            Showing {total === 0 ? 0 : skip + 1}-
            {Math.min(skip + limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <span>Rows</span>
            <select
              className="h-9 rounded-md border border-[#eae7ec] px-2"
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setSkip(0);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              className="h-9 rounded-md border border-[#eae7ec] px-3 disabled:opacity-50"
              disabled={skip === 0 || loading}
              onClick={() => setSkip(Math.max(0, skip - limit))}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="h-9 rounded-md border border-[#eae7ec] px-3 disabled:opacity-50"
              disabled={skip + limit >= total || loading}
              onClick={() => setSkip(skip + limit)}
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-[14px] text-[#84828e]">Loading...</div>
        ) : (
          <>
            <div className="w-full max-w-full overflow-x-auto">
              <Table className="min-w-[1520px] table-fixed">
                <TableHeader>
                  <TableRow className="border-b-0">
                    <TableHead className="w-[150px] bg-[#faf9fb]">Created</TableHead>
                    <TableHead className="w-[340px] bg-[#faf9fb]">Alert</TableHead>
                    <TableHead className="w-[190px] bg-[#faf9fb]">Workspace</TableHead>
                    <TableHead className="w-[160px] bg-[#faf9fb]">Workspace Name</TableHead>
                    <TableHead className="w-[120px] bg-[#faf9fb]">Severity</TableHead>
                    <TableHead className="w-[130px] bg-[#faf9fb]">Status</TableHead>
                    <TableHead className="w-[120px] bg-[#faf9fb]">Amount</TableHead>
                    <TableHead className="w-[170px] bg-[#faf9fb]">Invoice</TableHead>
                    <TableHead className="w-[260px] bg-[#faf9fb]">Actions</TableHead>
                    <TableHead className="w-[80px] bg-[#faf9fb]">Details</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {alerts.map((alert) => {
                    const isExpanded = expandedAlertId === alert._id;

                    return (
                      <React.Fragment key={alert._id}>
                        <TableRow>
                          <TableCell className="whitespace-nowrap">
                            {formatDateTime(alert.createdAt)}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-[320px]">
                              <div className="font-medium text-[#211f26]">
                                {alert.title || alert.alertType}
                              </div>
                              <div className="whitespace-normal text-[12px] text-[#84828e]">
                                {alert.description || "-"}
                              </div>
                              <div className="text-[12px] text-[#5f5a66]">
                                {alert.alertType}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="break-words">{alert.workspaceId || "-"}</TableCell>
                          <TableCell className="break-words">
                            {alert.workspaceId
                              ? workspaceNames[alert.workspaceId] || "-"
                              : "-"}
                          </TableCell>
                          <TableCell>{alert.severity || "-"}</TableCell>
                          <TableCell>
                            <span
                              className={`rounded-full px-2 py-1 text-[12px] ${getStatusClassName(
                                alert.status,
                              )}`}
                            >
                              {alert.status}
                            </span>
                          </TableCell>
                          <TableCell>
                            {typeof alert.amount === "number"
                              ? `${alert.currency || ""} ${alert.amount / 100}`
                              : "-"}
                          </TableCell>
                          <TableCell className="break-words">{alert.stripeInvoiceId || "-"}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              {getActionsForAlert(alert).map((action) => (
                                <button
                                  key={action}
                                  className="rounded-md border border-[#eae7ec] px-2 py-1 text-[12px] text-[#211f26] hover:bg-[#faf9fb] disabled:opacity-60"
                                  disabled={actionLoadingId === `${alert._id}-${action}`}
                                  title={actionTooltips[action] || actionLabels[action]}
                                  aria-label={actionTooltips[action] || actionLabels[action]}
                                  onClick={() => handleAction(alert, action)}
                                >
                                  {actionLoadingId === `${alert._id}-${action}`
                                    ? "..."
                                    : actionLabels[action]}
                                </button>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <button
                              className="rounded-md border border-[#eae7ec] px-2 py-1 text-[12px] text-[#211f26] hover:bg-[#faf9fb]"
                              title={isExpanded ? "Hide full billing alert document" : "Show full billing alert document"}
                              aria-expanded={isExpanded}
                              onClick={() =>
                                setExpandedAlertId(isExpanded ? null : alert._id)
                              }
                            >
                              {isExpanded ? "Hide" : "View"}
                            </button>
                          </TableCell>
                        </TableRow>

                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={10} className="whitespace-normal bg-white p-0">
                              <div className="border-t border-[#eae7ec] bg-[#fbfafc] p-4">
                                <div className="mb-3 text-[13px] font-medium text-[#5f5a66]">
                                  Billing alert document
                                </div>
                                <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-[#eae7ec] bg-white p-3 text-[12px] leading-5 text-[#211f26]">
                                  {JSON.stringify(alert, null, 2)}
                                </pre>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {alerts.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                No billing alerts found.
              </div>
            )}
          </>
        )}
      </div>

      {confirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[88vh] w-full max-w-[720px] overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="border-b border-[#eae7ec] px-5 py-4">
              <h2 className="text-[18px] font-semibold text-[#211f26]">
                Confirm {actionLabels[confirmation.action]}
              </h2>
              <p className="mt-1 text-[13px] text-[#84828e]">
                Review the recipients and email body before sending.
              </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              <div className="grid gap-3 text-[14px]">
                <div>
                  <div className="text-[12px] font-medium uppercase text-[#84828e]">
                    Send to
                  </div>
                  <div className="break-words text-[#211f26]">
                    {(confirmation.preview?.to || []).join(", ") || "-"}
                  </div>
                </div>

                <div>
                  <div className="text-[12px] font-medium uppercase text-[#84828e]">
                    Subject
                  </div>
                  <div className="break-words text-[#211f26]">
                    {confirmation.preview?.subject || "-"}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 rounded-md border border-[#eae7ec] bg-[#faf9fb] p-3 text-[13px]">
                  <div>
                    <span className="text-[#84828e]">Alert Type: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.alertType ||
                        confirmation.alert.alertType ||
                        "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#84828e]">Workspace: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.workspaceId ||
                        confirmation.alert.workspaceId ||
                        "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#84828e]">Customer: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.customerEmail ||
                        confirmation.alert.customerEmail ||
                        confirmation.alert.customerId ||
                        "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#84828e]">Customer ID: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.customerId ||
                        confirmation.alert.customerId ||
                        "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#84828e]">Stripe Customer: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.stripeCustomerId ||
                        confirmation.alert.stripeCustomerId ||
                        "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#84828e]">Subscription: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.stripeSubscriptionId ||
                        confirmation.alert.stripeSubscriptionId ||
                        "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#84828e]">Invoice: </span>
                    <span className="text-[#211f26]">
                      {confirmation.preview?.details?.stripeInvoiceId ||
                        confirmation.alert.stripeInvoiceId ||
                        "-"}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="mb-2 text-[12px] font-medium uppercase text-[#84828e]">
                    Email Body
                  </div>
                  <pre className="max-h-[260px] whitespace-pre-wrap break-words rounded-md border border-[#eae7ec] bg-white p-3 text-[13px] leading-5 text-[#211f26]">{confirmation.preview?.text || "-"}</pre>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-[#eae7ec] px-5 py-4">
              <button
                className="h-10 rounded-md border border-[#eae7ec] px-4 text-[14px] text-[#211f26] disabled:opacity-60"
                disabled={actionLoadingId !== null}
                onClick={() => setConfirmation(null)}
              >
                Cancel
              </button>
              <button
                className="h-10 rounded-md bg-[#211f26] px-4 text-[14px] text-white disabled:opacity-60"
                disabled={
                  actionLoadingId ===
                  `${confirmation.alert._id}-${confirmation.action}`
                }
                onClick={handleConfirmAction}
              >
                {actionLoadingId ===
                `${confirmation.alert._id}-${confirmation.action}`
                  ? "Sending..."
                  : confirmation.action === "NOTIFY_ADMIN"
                    ? "Confirm Notify Admin"
                    : "Confirm Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
